// ═══════════════════════════════════════════════════════════════════
//  TOQUM · PROJECT MODULE — DMAIC Quality Improvement Projects
//
//  Flow:
//  1. QA/Manager raises Project Request → manager approves
//  2. Approved: team selected (internal QA + supplier contacts)
//  3. Training check: team undergoes project management + relevant training
//     (exempted if already completed for a prior project)
//  4. DMAIC phases: Define → Measure → Analyze → Improve → Control
//  5. Each phase has structured forms, file uploads, sign-offs
//  6. On Control phase complete → AI generates cost-benefit + case study
//  7. Stored as success story → broadcast via Andon
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo, useCallback } from 'react';
import {
  T, uid, today, num, Section, Field, Grid, Btn, DataTable, TabBar,
  Tag, Badge, StatCard, Collapse, Modal, Toast, isManager, API_URL, uploadFile,
} from './Shared';

// ── Phase colours ────────────────────────────────────────────────
const PHASE_COLOR = {
  Define:'#0073ea', Measure:'#a25ddc', Analyze:'#fdab3d',
  Improve:'#00c875', Control:'#0da99b',
};
const PHASES = ['Define','Measure','Analyze','Improve','Control'];

// ── Project status colours ────────────────────────────────────────
const STATUS_COLOR = {
  'Draft':'#8a94a2', 'Pending Approval':'#fdab3d', 'Approved':'#0073ea',
  'Training':'#a25ddc', 'In Progress':'#00c875', 'Control':'#0da99b',
  'Completed':'#0a7d43', 'Cancelled':'#e2445c', 'On Hold':'#64748b',
};

// ── Root cause categories (common in garment QA) ─────────────────
const ROOT_CAUSE_CATS = [
  'Man (Skill Gap)','Machine (Equipment)','Method (Process)','Material (Fabric/Trims)',
  'Measurement (Spec/Tolerance)','Environment (Workspace)',
];

// ── Training courses relevant to project types ───────────────────
const RECOMMENDED_TRAINING = {
  'Skip Stitch':       ['Stitch Quality Control','Machine Maintenance Basics','DMAIC Project Management'],
  'Measurement':       ['Measurement Technique','Spec Sheet Reading','DMAIC Project Management'],
  'Seam Slippage':     ['Fabric Behaviour','Seam Construction','DMAIC Project Management'],
  'Color Bleeding':    ['Fabric Testing','Color Fastness','DMAIC Project Management'],
  'AQL Failure':       ['AQL & Sampling','Defect Classification','DMAIC Project Management'],
  'Packing Defects':   ['Packing Standards','Carton Marking','DMAIC Project Management'],
  'Shrinkage':         ['Fabric Pre-treatment','Shrinkage Testing','DMAIC Project Management'],
  'Workmanship':       ['Workmanship Standards','Self-inspection','DMAIC Project Management'],
  'Other':             ['DMAIC Project Management'],
};

// ── Shared small components ───────────────────────────────────────
const PhasePill = ({ phase, active, done, onClick }) => (
  <button onClick={onClick} style={{
    padding:'7px 16px', border:'none', borderRadius:20, cursor:'pointer',
    fontSize:11.5, fontWeight:800, transition:'all .15s',
    background: done ? PHASE_COLOR[phase] : active ? PHASE_COLOR[phase] : '#f1f5f9',
    color: done||active ? 'white' : '#676879',
    opacity: done ? 0.85 : 1,
    boxShadow: active ? `0 2px 10px ${PHASE_COLOR[phase]}44` : 'none',
  }}>
    {done ? '✓ ' : ''}{phase}
  </button>
);

const FieldRow = ({ children }) => (
  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12, marginBottom:12 }}>
    {children}
  </div>
);

const FInput = ({ label, value, onChange, type='text', placeholder='', required=false, opts=null }) => (
  <div>
    <label style={{ fontSize:11, fontWeight:700, color:'#676879', display:'block', marginBottom:3 }}>
      {label}{required&&<span style={{color:T.danger}}> *</span>}
    </label>
    {opts
      ? <select value={value} onChange={onChange}
          style={{ width:'100%', padding:'8px 10px', border:`1.5px solid #e6e9ef`,
            borderRadius:7, fontSize:13, outline:'none', background:'white' }}>
          <option value="">— Select —</option>
          {opts.map(o=><option key={o}>{o}</option>)}
        </select>
      : type==='textarea'
        ? <textarea value={value} onChange={onChange} rows={3} placeholder={placeholder}
            style={{ width:'100%', padding:'8px 10px', border:`1.5px solid #e6e9ef`,
              borderRadius:7, fontSize:12.5, outline:'none', resize:'vertical', boxSizing:'border-box' }}/>
        : <input type={type} value={value} onChange={onChange} placeholder={placeholder}
            style={{ width:'100%', padding:'8px 10px', border:`1.5px solid #e6e9ef`,
              borderRadius:7, fontSize:13, outline:'none' }}/>
    }
  </div>
);

// ── Phase form components ─────────────────────────────────────────

// DEFINE
function DefineForm({ data, onChange }) {
  const f = data || {};
  const set = (k,v) => onChange({...f,[k]:v});
  return (
    <div>
      <div style={{fontSize:11,color:'#676879',marginBottom:12,background:'#eef4ff',
        borderRadius:6,padding:'8px 12px'}}>
        <b>Define Phase:</b> Clearly state the problem, scope, goal, team and timeline.
        A well-defined problem is half solved.
      </div>
      <FieldRow>
        <FInput label="Problem Statement *" value={f.problemStatement||''} required
          onChange={e=>set('problemStatement',e.target.value)} type="textarea"
          placeholder="e.g. Skip stitch defect rate is 8.2% on style XYZ at Sunrise Garments, causing 34% inspection failure rate over last 3 months"/>
        <FInput label="Business Impact / Why Now" value={f.impact||''} type="textarea"
          onChange={e=>set('impact',e.target.value)}
          placeholder="e.g. ₹2.4L rework cost, 3 shipment delays, customer complaint from H&M"/>
      </FieldRow>
      <FieldRow>
        <FInput label="Goal Statement (SMART) *" value={f.goalStatement||''} required type="textarea"
          onChange={e=>set('goalStatement',e.target.value)}
          placeholder="e.g. Reduce skip stitch defect rate from 8.2% to below 1% by 30-Sep-2026"/>
        <FInput label="Scope (what's IN)" value={f.scopeIn||''} type="textarea"
          onChange={e=>set('scopeIn',e.target.value)}
          placeholder="e.g. All sewing lines producing Style XYZ at Sunrise factory"/>
      </FieldRow>
      <FieldRow>
        <FInput label="Scope (what's OUT)" value={f.scopeOut||''} type="textarea"
          onChange={e=>set('scopeOut',e.target.value)}
          placeholder="e.g. Other styles, other factories"/>
        <FInput label="Customer / Stakeholder Impact" value={f.customer||''} type="textarea"
          onChange={e=>set('customer',e.target.value)}
          placeholder="e.g. H&M raised 2 complaints. Risk of order cancellation."/>
      </FieldRow>
      <FieldRow>
        <FInput label="Project Start Date" value={f.startDate||today()} type="date"
          onChange={e=>set('startDate',e.target.value)}/>
        <FInput label="Target Completion Date *" value={f.targetDate||''} required type="date"
          onChange={e=>set('targetDate',e.target.value)}/>
        <FInput label="Review Frequency" value={f.reviewFreq||''} opts={['Weekly','Bi-weekly','Monthly']}
          onChange={e=>set('reviewFreq',e.target.value)}/>
      </FieldRow>
      <div style={{marginBottom:12}}>
        <label style={{fontSize:11,fontWeight:700,color:'#676879',display:'block',marginBottom:4}}>
          SIPOC Summary <small style={{fontWeight:400}}>(Supplier → Input → Process → Output → Customer)</small>
        </label>
        <div style={{overflowX:'auto'}}>
          <table style={{borderCollapse:'collapse',width:'100%',fontSize:12}}>
            <thead><tr style={{background:'linear-gradient(90deg,#0073ea,#005bbf)',color:'white'}}>
              {['Supplier','Input','Process','Output','Customer'].map(h=>(
                <th key={h} style={{padding:'7px 10px',textAlign:'left',fontWeight:700}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{(f.sipoc||[['','','','','']]).map((row,i)=>(
              <tr key={i} style={{borderBottom:'1px solid #e6e9ef'}}>
                {row.map((cell,j)=>(
                  <td key={j} style={{padding:'4px 6px'}}>
                    <input value={cell} onChange={e=>{
                      const rows = (f.sipoc||[['','','','','']]).map((r,ri)=>
                        ri===i ? r.map((c,ci)=>ci===j?e.target.value:c) : r);
                      set('sipoc',rows);
                    }} style={{width:'100%',border:'1px solid #e6e9ef',borderRadius:4,
                      padding:'5px 7px',fontSize:11.5}}/>
                  </td>
                ))}
              </tr>
            ))}
            </tbody>
          </table>
        </div>
        <button onClick={()=>set('sipoc',[...(f.sipoc||[['','','','','']]),['','','','','']])}
          style={{marginTop:6,padding:'5px 12px',background:'white',border:'1px solid #0073ea',
            color:'#0073ea',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer'}}>
          + Add Row
        </button>
      </div>
    </div>
  );
}

// MEASURE
function MeasureForm({ data, onChange }) {
  const f = data || {};
  const set = (k,v) => onChange({...f,[k]:v});
  return (
    <div>
      <div style={{fontSize:11,color:'#676879',marginBottom:12,background:'#f5eeff',
        borderRadius:6,padding:'8px 12px'}}>
        <b>Measure Phase:</b> Collect baseline data. Establish the current process capability
        before making any changes.
      </div>
      <FieldRow>
        <FInput label="Primary Metric (Y)" value={f.primaryMetric||''} type="textarea"
          onChange={e=>set('primaryMetric',e.target.value)}
          placeholder="e.g. Skip stitch defect rate (defects per 100 units)"/>
        <FInput label="Secondary Metrics" value={f.secondaryMetrics||''} type="textarea"
          onChange={e=>set('secondaryMetrics',e.target.value)}
          placeholder="e.g. First-pass rate, rework time per garment"/>
      </FieldRow>
      <FieldRow>
        <FInput label="Baseline Value (Before)" value={f.baseline||''}
          onChange={e=>set('baseline',e.target.value)} placeholder="e.g. 8.2%"/>
        <FInput label="Measurement Period" value={f.measurePeriod||''}
          onChange={e=>set('measurePeriod',e.target.value)} placeholder="e.g. 01-Apr to 30-Jun-2026"/>
        <FInput label="Data Source" value={f.dataSource||''}
          onChange={e=>set('dataSource',e.target.value)} placeholder="e.g. Toqum inspection records"/>
        <FInput label="Sample Size" value={f.sampleSize||''}
          onChange={e=>set('sampleSize',e.target.value)} placeholder="e.g. 2,400 garments"/>
      </FieldRow>

      {/* Data table */}
      <div style={{marginBottom:12}}>
        <label style={{fontSize:11,fontWeight:700,color:'#676879',display:'block',marginBottom:4}}>
          Data Collection Table (Week / Metric / Value / Notes)
        </label>
        <div style={{overflowX:'auto'}}>
          <table style={{borderCollapse:'collapse',width:'100%',fontSize:12}}>
            <thead><tr style={{background:'#f1f5f9'}}>
              {['Period','Units Checked','Defects Found','Defect Rate %','Notes'].map(h=>(
                <th key={h} style={{padding:'6px 9px',textAlign:'left',fontWeight:800,
                  borderBottom:'1px solid #e6e9ef',fontSize:11}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{(f.dataRows||[{period:'',checked:'',defects:'',rate:'',notes:''}]).map((row,i)=>(
              <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                {['period','checked','defects','rate','notes'].map(k=>(
                  <td key={k} style={{padding:'4px 5px'}}>
                    <input value={row[k]||''} onChange={e=>{
                      const rows=(f.dataRows||[{}]).map((r,ri)=>ri===i?{...r,[k]:e.target.value}:r);
                      set('dataRows',rows);
                    }} style={{width:'100%',border:'1px solid #e6e9ef',borderRadius:4,
                      padding:'5px 7px',fontSize:11.5}}/>
                  </td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div>
        <button onClick={()=>set('dataRows',[...(f.dataRows||[{}]),{period:'',checked:'',defects:'',rate:'',notes:''}])}
          style={{marginTop:6,padding:'5px 12px',background:'white',border:'1px solid #a25ddc',
            color:'#a25ddc',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer'}}>
          + Add Row
        </button>
      </div>

      <FieldRow>
        <FInput label="Process Capability (Sigma Level / Cpk)" value={f.capability||''}
          onChange={e=>set('capability',e.target.value)} placeholder="e.g. 1.8 sigma"/>
        <FInput label="MSA / Measurement System Analysis" value={f.msa||''} type="textarea"
          onChange={e=>set('msa',e.target.value)}
          placeholder="e.g. Gauge R&R not applicable — attribute data (pass/fail per garment)"/>
      </FieldRow>
      <FInput label="Measure Phase Summary & Key Findings" value={f.summary||''} type="textarea"
        onChange={e=>set('summary',e.target.value)}
        placeholder="Summarise what the data tells you about the current state"/>
    </div>
  );
}

// ANALYZE
function AnalyzeForm({ data, onChange }) {
  const f = data || {};
  const set = (k,v) => onChange({...f,[k]:v});
  return (
    <div>
      <div style={{fontSize:11,color:'#676879',marginBottom:12,background:'#fff8e6',
        borderRadius:6,padding:'8px 12px'}}>
        <b>Analyze Phase:</b> Identify root causes using data. Do not jump to solutions yet.
        Validate root causes with evidence.
      </div>

      {/* Fishbone / Ishikawa */}
      <div style={{marginBottom:16}}>
        <label style={{fontSize:11,fontWeight:800,color:'#676879',display:'block',marginBottom:6}}>
          Root Cause Analysis — Fishbone (Ishikawa) Diagram
        </label>
        {ROOT_CAUSE_CATS.map(cat=>(
          <div key={cat} style={{marginBottom:8}}>
            <div style={{fontSize:11.5,fontWeight:700,color:T.textMain,marginBottom:4,
              display:'flex',alignItems:'center',gap:6}}>
              <span style={{width:8,height:8,borderRadius:'50%',background:T.primary,display:'inline-block'}}/>
              {cat}
            </div>
            <textarea value={(f.fishbone||{})[cat]||''} rows={2}
              onChange={e=>set('fishbone',{...(f.fishbone||{}),[cat]:e.target.value})}
              placeholder={`List causes under ${cat}…`}
              style={{width:'100%',padding:'7px 10px',border:'1.5px solid #e6e9ef',
                borderRadius:6,fontSize:12,resize:'vertical',boxSizing:'border-box'}}/>
          </div>
        ))}
      </div>

      {/* 5 Whys */}
      <div style={{marginBottom:16}}>
        <label style={{fontSize:11,fontWeight:800,color:'#676879',display:'block',marginBottom:6}}>
          5 Whys Analysis
        </label>
        {[1,2,3,4,5].map(n=>(
          <div key={n} style={{display:'flex',gap:8,alignItems:'flex-start',marginBottom:6}}>
            <div style={{width:24,height:24,borderRadius:'50%',background:'#fdab3d',color:'white',
              display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,
              fontWeight:900,flexShrink:0,marginTop:8}}>
              {n}
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:10.5,color:'#676879',marginBottom:2}}>Why #{n}</div>
              <input value={(f.fiveWhys||{})[n]||''}
                onChange={e=>set('fiveWhys',{...(f.fiveWhys||{}),[n]:e.target.value})}
                placeholder={n===1?'Why does the problem occur?':`Why does [answer ${n-1}] happen?`}
                style={{width:'100%',padding:'7px 10px',border:'1.5px solid #e6e9ef',
                  borderRadius:6,fontSize:12}}/>
            </div>
          </div>
        ))}
      </div>

      {/* Root cause validation */}
      <FieldRow>
        <FInput label="Root Cause (Validated)" value={f.rootCause||''} type="textarea" required
          onChange={e=>set('rootCause',e.target.value)}
          placeholder="State the confirmed root cause(s) with evidence"/>
        <FInput label="Evidence / Data Supporting Root Cause" value={f.evidence||''} type="textarea"
          onChange={e=>set('evidence',e.target.value)}
          placeholder="e.g. 78% of skip stitches found on L-04, machine #7 — maintenance log shows 14 months since service"/>
      </FieldRow>

      {/* Pareto */}
      <div style={{marginBottom:12}}>
        <label style={{fontSize:11,fontWeight:800,color:'#676879',display:'block',marginBottom:6}}>
          Pareto Analysis (Top Causes by Frequency)
        </label>
        <div style={{overflowX:'auto'}}>
          <table style={{borderCollapse:'collapse',width:'100%',fontSize:12}}>
            <thead><tr style={{background:'#f1f5f9'}}>
              {['Cause','Count','%','Cumulative %','Vital Few?'].map(h=>(
                <th key={h} style={{padding:'6px 9px',textAlign:'left',fontWeight:800,
                  borderBottom:'1px solid #e6e9ef',fontSize:11}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{(f.pareto||[{cause:'',count:'',pct:'',cum:'',vital:'Yes'}]).map((row,i)=>(
              <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                {['cause','count','pct','cum'].map(k=>(
                  <td key={k} style={{padding:'4px 5px'}}>
                    <input value={row[k]||''} onChange={e=>{
                      const rows=(f.pareto||[{}]).map((r,ri)=>ri===i?{...r,[k]:e.target.value}:r);
                      set('pareto',rows);
                    }} style={{width:'100%',border:'1px solid #e6e9ef',borderRadius:4,
                      padding:'5px 7px',fontSize:11.5}}/>
                  </td>
                ))}
                <td style={{padding:'4px 5px'}}>
                  <select value={row.vital||'Yes'} onChange={e=>{
                    const rows=(f.pareto||[{}]).map((r,ri)=>ri===i?{...r,vital:e.target.value}:r);
                    set('pareto',rows);
                  }} style={{padding:'5px',border:'1px solid #e6e9ef',borderRadius:4,fontSize:11}}>
                    <option>Yes</option><option>No</option>
                  </select>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <button onClick={()=>set('pareto',[...(f.pareto||[{}]),{cause:'',count:'',pct:'',cum:'',vital:'No'}])}
          style={{marginTop:6,padding:'5px 12px',background:'white',border:'1px solid #fdab3d',
            color:'#fdab3d',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer'}}>
          + Add Row
        </button>
      </div>

      <FInput label="Analyze Phase Conclusion" value={f.conclusion||''} type="textarea"
        onChange={e=>set('conclusion',e.target.value)}
        placeholder="Summarise validated root causes and which will be addressed in Improve phase"/>
    </div>
  );
}

// IMPROVE
function ImproveForm({ data, onChange }) {
  const f = data || {};
  const set = (k,v) => onChange({...f,[k]:v});
  return (
    <div>
      <div style={{fontSize:11,color:'#676879',marginBottom:12,background:'#edfff5',
        borderRadius:6,padding:'8px 12px'}}>
        <b>Improve Phase:</b> Generate, evaluate and implement solutions.
        Pilot test before full rollout.
      </div>

      {/* Solution brainstorm */}
      <div style={{marginBottom:14}}>
        <label style={{fontSize:11,fontWeight:800,color:'#676879',display:'block',marginBottom:6}}>
          Solution Ideas (Brainstorm — list all ideas, evaluate later)
        </label>
        <div style={{overflowX:'auto'}}>
          <table style={{borderCollapse:'collapse',width:'100%',fontSize:12}}>
            <thead><tr style={{background:'linear-gradient(90deg,#00c875,#009d5a)',color:'white'}}>
              {['Solution Idea','Ease (1-5)','Impact (1-5)','Score','Selected?'].map(h=>(
                <th key={h} style={{padding:'7px 9px',textAlign:'left',fontWeight:700}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{(f.solutions||[{idea:'',ease:'',impact:'',selected:'Yes'}]).map((row,i)=>{
              const score = (parseInt(row.ease)||0) * (parseInt(row.impact)||0);
              return <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                <td style={{padding:'4px 5px'}}>
                  <input value={row.idea||''} onChange={e=>{
                    const rows=(f.solutions||[{}]).map((r,ri)=>ri===i?{...r,idea:e.target.value}:r);
                    set('solutions',rows);
                  }} style={{width:'100%',border:'1px solid #e6e9ef',borderRadius:4,padding:'5px 7px',fontSize:11.5}}/>
                </td>
                {['ease','impact'].map(k=>(
                  <td key={k} style={{padding:'4px 5px'}}>
                    <input type="number" min="1" max="5" value={row[k]||''}
                      onChange={e=>{
                        const rows=(f.solutions||[{}]).map((r,ri)=>ri===i?{...r,[k]:e.target.value}:r);
                        set('solutions',rows);
                      }} style={{width:55,border:'1px solid #e6e9ef',borderRadius:4,padding:'5px 7px',fontSize:11.5,textAlign:'center'}}/>
                  </td>
                ))}
                <td style={{padding:'4px 10px',fontWeight:800,color:score>=15?T.success:score>=8?T.warning:T.danger}}>
                  {score||'—'}
                </td>
                <td style={{padding:'4px 5px'}}>
                  <select value={row.selected||'No'} onChange={e=>{
                    const rows=(f.solutions||[{}]).map((r,ri)=>ri===i?{...r,selected:e.target.value}:r);
                    set('solutions',rows);
                  }} style={{padding:'5px',border:'1px solid #e6e9ef',borderRadius:4,fontSize:11}}>
                    <option>Yes</option><option>No</option>
                  </select>
                </td>
              </tr>;
            })}</tbody>
          </table>
        </div>
        <button onClick={()=>set('solutions',[...(f.solutions||[{}]),{idea:'',ease:'',impact:'',selected:'No'}])}
          style={{marginTop:6,padding:'5px 12px',background:'white',border:'1px solid #00c875',
            color:'#00c875',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer'}}>
          + Add Idea
        </button>
      </div>

      {/* Implementation plan */}
      <div style={{marginBottom:14}}>
        <label style={{fontSize:11,fontWeight:800,color:'#676879',display:'block',marginBottom:6}}>
          Implementation Plan (Action Plan)
        </label>
        <div style={{overflowX:'auto'}}>
          <table style={{borderCollapse:'collapse',width:'100%',fontSize:12}}>
            <thead><tr style={{background:'#f1f5f9'}}>
              {['Action','Owner','Due Date','Status','Result'].map(h=>(
                <th key={h} style={{padding:'6px 9px',textAlign:'left',fontWeight:800,
                  borderBottom:'1px solid #e6e9ef',fontSize:11}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{(f.actions||[{action:'',owner:'',due:'',status:'Pending',result:''}]).map((row,i)=>(
              <tr key={i} style={{borderBottom:'1px solid #f1f5f9',
                background:row.status==='Done'?'#f0fdf4':row.status==='Overdue'?'#fdeaec':'white'}}>
                <td style={{padding:'4px 5px'}}>
                  <input value={row.action||''} onChange={e=>{
                    const rows=(f.actions||[{}]).map((r,ri)=>ri===i?{...r,action:e.target.value}:r);
                    set('actions',rows);
                  }} style={{width:'100%',border:'1px solid #e6e9ef',borderRadius:4,padding:'5px 7px',fontSize:11.5}}/>
                </td>
                <td style={{padding:'4px 5px'}}>
                  <input value={row.owner||''} onChange={e=>{
                    const rows=(f.actions||[{}]).map((r,ri)=>ri===i?{...r,owner:e.target.value}:r);
                    set('actions',rows);
                  }} style={{width:90,border:'1px solid #e6e9ef',borderRadius:4,padding:'5px 7px',fontSize:11.5}}/>
                </td>
                <td style={{padding:'4px 5px'}}>
                  <input type="date" value={row.due||''} onChange={e=>{
                    const rows=(f.actions||[{}]).map((r,ri)=>ri===i?{...r,due:e.target.value}:r);
                    set('actions',rows);
                  }} style={{border:'1px solid #e6e9ef',borderRadius:4,padding:'5px 7px',fontSize:11}}/>
                </td>
                <td style={{padding:'4px 5px'}}>
                  <select value={row.status||'Pending'} onChange={e=>{
                    const rows=(f.actions||[{}]).map((r,ri)=>ri===i?{...r,status:e.target.value}:r);
                    set('actions',rows);
                  }} style={{padding:'5px',border:'1px solid #e6e9ef',borderRadius:4,fontSize:11}}>
                    {['Pending','In Progress','Done','Overdue','Cancelled'].map(s=><option key={s}>{s}</option>)}
                  </select>
                </td>
                <td style={{padding:'4px 5px'}}>
                  <input value={row.result||''} onChange={e=>{
                    const rows=(f.actions||[{}]).map((r,ri)=>ri===i?{...r,result:e.target.value}:r);
                    set('actions',rows);
                  }} style={{width:'100%',border:'1px solid #e6e9ef',borderRadius:4,padding:'5px 7px',fontSize:11.5}}/>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <button onClick={()=>set('actions',[...(f.actions||[{}]),{action:'',owner:'',due:'',status:'Pending',result:''}])}
          style={{marginTop:6,padding:'5px 12px',background:'white',border:'1px solid #00c875',
            color:'#00c875',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer'}}>
          + Add Action
        </button>
      </div>

      <FieldRow>
        <FInput label="Pilot Test Results" value={f.pilotResults||''} type="textarea"
          onChange={e=>set('pilotResults',e.target.value)}
          placeholder="What happened during pilot? Was improvement confirmed?"/>
        <FInput label="Result After Implementation (Metric Y)" value={f.resultAfter||''}
          onChange={e=>set('resultAfter',e.target.value)}
          placeholder="e.g. Skip stitch rate dropped to 0.8%"/>
      </FieldRow>
    </div>
  );
}

// CONTROL
function ControlForm({ data, onChange }) {
  const f = data || {};
  const set = (k,v) => onChange({...f,[k]:v});
  return (
    <div>
      <div style={{fontSize:11,color:'#676879',marginBottom:12,background:'#edfff9',
        borderRadius:6,padding:'8px 12px'}}>
        <b>Control Phase:</b> Lock in the gains. Document the new standard process and
        set up monitoring to ensure the problem doesn't return.
      </div>
      <FieldRow>
        <FInput label="Result Achieved (Final Metric)" value={f.finalResult||''} required
          onChange={e=>set('finalResult',e.target.value)}
          placeholder="e.g. Skip stitch rate: 0.6% (target was 1%)"/>
        <FInput label="% Improvement Achieved" value={f.improvement||''}
          onChange={e=>set('improvement',e.target.value)} placeholder="e.g. 92.7%"/>
        <FInput label="Sustained For (weeks)" value={f.sustainedWeeks||''}
          onChange={e=>set('sustainedWeeks',e.target.value)} placeholder="e.g. 8 weeks"/>
      </FieldRow>
      <FieldRow>
        <FInput label="Control Plan / Standard Operating Procedure" value={f.controlPlan||''} type="textarea" required
          onChange={e=>set('controlPlan',e.target.value)}
          placeholder="Describe the new standard: e.g. Machine service every 3 months, tension check daily, skip stitch inspection at cutting stage"/>
        <FInput label="Monitoring Method & Frequency" value={f.monitoring||''} type="textarea"
          onChange={e=>set('monitoring',e.target.value)}
          placeholder="e.g. Daily QA check of 50 garments per line, Toqum weekly KPI review"/>
      </FieldRow>
      <FieldRow>
        <FInput label="Response Plan (if problem returns)" value={f.responsePlan||''} type="textarea"
          onChange={e=>set('responsePlan',e.target.value)}
          placeholder="e.g. If rate exceeds 2%, escalate to Line Supervisor + QA Manager within 24h"/>
        <FInput label="Documents Updated / Created" value={f.docsUpdated||''} type="textarea"
          onChange={e=>set('docsUpdated',e.target.value)}
          placeholder="e.g. Machine maintenance SOP v2, QA daily check sheet"/>
      </FieldRow>

      {/* Cost Benefit */}
      <div style={{marginBottom:14,background:'white',border:'1px solid #e6e9ef',borderRadius:8,padding:'12px 14px'}}>
        <div style={{fontSize:11,fontWeight:800,textTransform:'uppercase',letterSpacing:'0.06em',
          color:'#676879',marginBottom:10}}>Cost-Benefit Analysis</div>
        <FieldRow>
          <FInput label="Cost of Poor Quality (Before)" value={f.copq||''} type="textarea"
            onChange={e=>set('copq',e.target.value)}
            placeholder="e.g. ₹2.4L rework, 3 shipment penalties, 14 re-inspection costs"/>
          <FInput label="Project Investment Cost" value={f.investment||''} type="textarea"
            onChange={e=>set('investment',e.target.value)}
            placeholder="e.g. Training: ₹45K, Machine service: ₹12K, QA time: ₹30K"/>
        </FieldRow>
        <FieldRow>
          <FInput label="Annual Savings Achieved" value={f.savings||''}
            onChange={e=>set('savings',e.target.value)} placeholder="e.g. ₹4.8L/year"/>
          <FInput label="ROI" value={f.roi||''}
            onChange={e=>set('roi',e.target.value)} placeholder="e.g. 540% in year 1"/>
          <FInput label="Payback Period" value={f.payback||''}
            onChange={e=>set('payback',e.target.value)} placeholder="e.g. 2.1 months"/>
        </FieldRow>
      </div>

      <FInput label="Lessons Learned" value={f.lessons||''} type="textarea"
        onChange={e=>set('lessons',e.target.value)}
        placeholder="What would you do differently? What worked especially well?"/>
      <FInput label="Recommendations for Other Suppliers / Projects" value={f.recommendations||''} type="textarea"
        onChange={e=>set('recommendations',e.target.value)}
        placeholder="Should this be rolled out to other factories? Similar defect patterns elsewhere?"/>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN MODULE
// ════════════════════════════════════════════════════════════════
export default function ProjectModule({ db, setDb, saveToDB, currentUser }) {
  const [view, setView]     = useState('list'); // list | request | detail | story
  const [selProj, setSelProj] = useState(null);
  const [selPhase, setSelPhase] = useState('Define');
  const [tab, setTab]       = useState('ACTIVE');
  const [toast, setToast]   = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const notify = useCallback((msg,type='success')=>{
    setToast({msg,type}); setTimeout(()=>setToast(null),3500);
  },[]);

  const projects  = db.qualityProjects || [];
  const stories   = db.successStories  || [];
  const allUsers  = db.users || [];
  const suppliers = (db.suppliers||[]).map(s=>s.name||s);
  const myCourses = db.trainingCourses || [];

  const myProjects = isManager(currentUser)
    ? projects
    : projects.filter(p =>
        p.requestedBy===currentUser.name ||
        (p.team||[]).some(t=>t.name===currentUser.name));

  const active    = myProjects.filter(p=>!['Completed','Cancelled'].includes(p.status));
  const completed = myProjects.filter(p=>p.status==='Completed');
  const pending   = projects.filter(p=>p.status==='Pending Approval');

  // ── Save project ───────────────────────────────────────────────
  const saveProject = async (proj) => {
    const upd = {...proj, updatedAt:new Date().toISOString()};
    setDb(p=>({...p, qualityProjects:
      [...(p.qualityProjects||[]).filter(x=>x.id!==upd.id), upd]}));
    await saveToDB('qualityProject', upd);
    setSelProj(upd);
  };

  // ── AI: generate case study ────────────────────────────────────
  const generateCaseStudy = async (proj) => {
    setAiLoading(true);
    try {
      const prompt = `You are a quality management expert. Generate a professional quality improvement case study and cost-benefit analysis based on this DMAIC project data:

Project: ${proj.title}
Supplier: ${(proj.suppliers||[]).join(', ')}
Problem: ${proj.phases?.Define?.problemStatement||''}
Goal: ${proj.phases?.Define?.goalStatement||''}
Root Cause: ${proj.phases?.Analyze?.rootCause||''}
Solution: ${(proj.phases?.Improve?.solutions||[]).filter(s=>s.selected==='Yes').map(s=>s.idea).join('; ')}
Result Before: ${proj.phases?.Measure?.baseline||''}
Result After: ${proj.phases?.Control?.finalResult||''}
Improvement: ${proj.phases?.Control?.improvement||''}
Savings: ${proj.phases?.Control?.savings||''}
ROI: ${proj.phases?.Control?.roi||''}
Lessons: ${proj.phases?.Control?.lessons||''}

Write a structured case study with:
1. Executive Summary (2-3 sentences, impact first)
2. The Challenge (problem context)
3. DMAIC Journey (brief each phase, key insight)
4. Results & Impact (numbers, before/after)
5. Cost-Benefit Analysis (table format)
6. Key Takeaways (3 bullet points)
7. Applicability to Other Suppliers

Keep it engaging, factual, and suitable for sharing with factory managers. Use ₹ for currency if amounts are given. Format with markdown headings.`;

      const res = await fetch(`${API_URL}/andon.php`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ role:'manager', user_name:currentUser.name,
          messages:[{role:'user',content:prompt}], context_summary:'DMAIC case study generation' })
      }).then(r=>r.json());

      const caseStudy = res.reply || res.content?.[0]?.text || 'Case study generation failed — check API key in andon.php';
      const upd = { ...proj, caseStudy, caseStudyGeneratedAt:new Date().toISOString() };
      await saveProject(upd);
      notify('Case study generated by AI', 'success');
    } catch(e) {
      notify('AI generation failed: '+e.message, 'warn');
    }
    setAiLoading(false);
  };

  // ── Share as success story ────────────────────────────────────
  const shareAsStory = async (proj) => {
    const story = {
      id: uid(), projectId: proj.id, title: proj.title,
      suppliers: proj.suppliers, category: proj.category,
      result: proj.phases?.Control?.finalResult,
      improvement: proj.phases?.Control?.improvement,
      savings: proj.phases?.Control?.savings,
      caseStudy: proj.caseStudy,
      sharedBy: currentUser.name, sharedAt: new Date().toISOString(),
    };
    setDb(p=>({...p, successStories:[...(p.successStories||[]),story]}));
    await saveToDB('successStory', story);
    // Broadcast via Andon system
    const msg = `🏆 New Success Story: "${proj.title}" — ${proj.phases?.Control?.improvement||''} improvement achieved at ${(proj.suppliers||[]).join(', ')}. See Projects → Success Stories.`;
    await fetch(`${API_URL}/chat_api.php`, {method:'POST',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({action:'send',room_id:'general',
        user_id:'system',user_name:'Toqum System',role:'admin',body:msg})});
    notify('Success story published & broadcast on Andon', 'success');
  };

  // ── Check training exemptions ─────────────────────────────────
  const getTrainingStatus = (member, courseTitle) => {
    const enrolments = db.trainingEnrolments || [];
    const attempts   = db.trainingAttempts   || [];
    const course     = myCourses.find(c=>c.title===courseTitle);
    if (!course) return { status:'Course not found', exempt:false };
    const enrol = enrolments.find(e=>e.userId===member.id&&e.courseId===course.id);
    const passed = attempts.some(a=>a.userId===member.id&&a.courseId===course.id&&a.passed);
    if (passed) return { status:'✓ Completed', exempt:true };
    if (enrol)  return { status:'Enrolled', exempt:false };
    return { status:'Not enrolled', exempt:false };
  };

  // ════════════════════════════════════════════════════════════════
  // REQUEST FORM
  // ════════════════════════════════════════════════════════════════
  const RequestForm = () => {
    const [f, setF] = useState({
      title:'', category:'Skip Stitch', suppliers:[], description:'',
      urgency:'Medium', kpi:'', baseline:'', goal:'', targetDate:'',
      affectedBrand:'', affectedPOs:'',
    });
    const [submitting, setSubmitting] = useState(false);

    const CATEGORIES = Object.keys(RECOMMENDED_TRAINING);
    const toggleSup = (s) => setF(p=>({...p, suppliers:
      p.suppliers.includes(s)?p.suppliers.filter(x=>x!==s):[...p.suppliers,s]}));

    const submit = async () => {
      if (!f.title||!f.suppliers.length||!f.description)
        return alert('Fill: title, at least one supplier, description');
      setSubmitting(true);
      const proj = {
        id: uid(), ...f,
        requestedBy: currentUser.name,
        requestedAt: new Date().toISOString(),
        status: isManager(currentUser) ? 'Approved' : 'Pending Approval',
        approvedBy: isManager(currentUser) ? currentUser.name : null,
        approvedAt: isManager(currentUser) ? new Date().toISOString() : null,
        team: [], phases:{Define:{},Measure:{},Analyze:{},Improve:{},Control:{}},
        currentPhase: 'Define', trainingRequired: RECOMMENDED_TRAINING[f.category]||['DMAIC Project Management'],
        updatedAt: new Date().toISOString(),
      };
      setDb(p=>({...p, qualityProjects:[...(p.qualityProjects||[]),proj]}));
      await saveToDB('qualityProject', proj);
      setSubmitting(false);
      notify(isManager(currentUser)?'Project created':'Request submitted for manager approval','success');
      setView('list');
    };

    return (
      <div style={{maxWidth:800,margin:'0 auto'}}>
        <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:16}}>
          <button onClick={()=>setView('list')}
            style={{background:'none',border:'none',color:T.primary,cursor:'pointer',
              fontSize:13,fontWeight:700}}>← Back</button>
          <h2 style={{margin:0,fontSize:16,fontWeight:900}}>New Improvement Project Request</h2>
        </div>

        <Section title="Project Overview">
          <FieldRow>
            <FInput label="Project Title *" value={f.title}
              onChange={e=>setF(p=>({...p,title:e.target.value}))} required
              placeholder="e.g. Skip Stitch Reduction — Sunrise Garments 2026"/>
            <FInput label="Problem Category *" value={f.category}
              onChange={e=>setF(p=>({...p,category:e.target.value}))} opts={CATEGORIES}/>
            <FInput label="Urgency" value={f.urgency}
              onChange={e=>setF(p=>({...p,urgency:e.target.value}))}
              opts={['Critical','High','Medium','Low']}/>
          </FieldRow>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:11,fontWeight:700,color:'#676879',display:'block',marginBottom:6}}>
              Target Supplier(s) * <small style={{fontWeight:400}}>(select one or more)</small>
            </label>
            <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
              {suppliers.map(s=>(
                <button key={s} onClick={()=>toggleSup(s)}
                  style={{padding:'6px 12px',borderRadius:20,border:`1.5px solid ${f.suppliers.includes(s)?T.primary:'#e6e9ef'}`,
                    background:f.suppliers.includes(s)?T.primary:'white',
                    color:f.suppliers.includes(s)?'white':'#323338',
                    fontSize:11.5,fontWeight:700,cursor:'pointer'}}>
                  {f.suppliers.includes(s)?'✓ ':''}{s}
                </button>
              ))}
            </div>
          </div>
          <FieldRow>
            <FInput label="Description / Background *" value={f.description}
              onChange={e=>setF(p=>({...p,description:e.target.value}))}
              type="textarea" required placeholder="What is the problem and why does it need a structured project?"/>
            <FInput label="Affected Brand" value={f.affectedBrand}
              onChange={e=>setF(p=>({...p,affectedBrand:e.target.value}))} placeholder="e.g. H&M"/>
          </FieldRow>
          <FieldRow>
            <FInput label="Current KPI / Baseline" value={f.baseline}
              onChange={e=>setF(p=>({...p,baseline:e.target.value}))} placeholder="e.g. 8.2% skip stitch rate"/>
            <FInput label="Improvement Goal" value={f.goal}
              onChange={e=>setF(p=>({...p,goal:e.target.value}))} placeholder="e.g. Reduce to below 1%"/>
            <FInput label="Target Date" value={f.targetDate} type="date"
              onChange={e=>setF(p=>({...p,targetDate:e.target.value}))}/>
          </FieldRow>
          <div style={{background:'#eef4ff',borderRadius:6,padding:'8px 12px',fontSize:11.5}}>
            <b>Recommended Training for {f.category}:</b>{' '}
            {(RECOMMENDED_TRAINING[f.category]||[]).join(' · ')}
          </div>
        </Section>

        <Btn label={submitting?'Submitting…':isManager(currentUser)?'Create Project':'Submit for Approval'}
          color={T.success} disabled={submitting} onClick={submit}/>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // PROJECT DETAIL
  // ════════════════════════════════════════════════════════════════
  const ProjectDetail = ({ proj }) => {
    const phaseComplete = (ph) => {
      const d = proj.phases?.[ph];
      if (!d) return false;
      if (ph==='Define')   return !!(d.problemStatement&&d.goalStatement&&d.targetDate);
      if (ph==='Measure')  return !!(d.baseline&&d.dataRows?.length);
      if (ph==='Analyze')  return !!(d.rootCause);
      if (ph==='Improve')  return !!(d.resultAfter);
      if (ph==='Control')  return !!(d.finalResult&&d.controlPlan);
      return false;
    };

    const allPhaseDone = PHASES.every(phaseComplete);
    const updatePhase = (ph, data) => saveProject({...proj, phases:{...proj.phases,[ph]:data}});
    const phaseIdx = PHASES.indexOf(selPhase);
    const phaseData = proj.phases?.[selPhase]||{};

    return (
      <div>
        {/* Back + status */}
        <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:16}}>
          <button onClick={()=>setView('list')}
            style={{background:'none',border:'none',color:T.primary,cursor:'pointer',fontSize:13,fontWeight:700}}>
            ← Projects
          </button>
          <h2 style={{margin:0,fontSize:16,fontWeight:900,flex:1}}>{proj.title}</h2>
          <span style={{padding:'4px 14px',borderRadius:10,fontSize:11.5,fontWeight:800,
            background:`${STATUS_COLOR[proj.status]||'#8a94a2'}18`,
            color:STATUS_COLOR[proj.status]||'#8a94a2'}}>
            {proj.status}
          </span>
        </div>

        {/* Project summary */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',
          gap:10,marginBottom:16}}>
          {[['Supplier(s)',(proj.suppliers||[]).join(', ')],
            ['Category',proj.category],['Requested By',proj.requestedBy],
            ['Target Date',proj.targetDate||'—'],['Urgency',proj.urgency||'—'],
            ['Current Phase',proj.currentPhase||'Define'],
          ].map(([l,v])=>(
            <div key={l} style={{background:'white',border:'1px solid #e6e9ef',borderRadius:8,padding:'10px 12px'}}>
              <div style={{fontSize:9.5,color:'#676879',textTransform:'uppercase',letterSpacing:'0.05em'}}>{l}</div>
              <div style={{fontSize:13,fontWeight:700,marginTop:2}}>{v}</div>
            </div>
          ))}
        </div>

        {/* Approval banner */}
        {proj.status==='Pending Approval' && isManager(currentUser) && (
          <div style={{background:'#fff8e6',border:'1px solid #fde68a',borderRadius:8,
            padding:'12px 16px',marginBottom:14,display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
            <div style={{flex:1}}>
              <b>Project Approval Required</b>
              <div style={{fontSize:12,marginTop:3,color:'#676879'}}>{proj.description}</div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <Btn label="✓ Approve" color={T.success} onClick={async()=>{
                const upd={...proj,status:'Approved',approvedBy:currentUser.name,approvedAt:new Date().toISOString()};
                await saveProject(upd); notify('Project approved');
              }}/>
              <Btn label="✕ Reject" color={T.danger} onClick={async()=>{
                const upd={...proj,status:'Cancelled'}; await saveProject(upd); setView('list');
              }}/>
            </div>
          </div>
        )}

        {/* Team */}
        {proj.status!=='Pending Approval' && (
          <Collapse title={`Project Team (${(proj.team||[]).length} members)`} defaultOpen>
            <div style={{marginBottom:10,display:'flex',flexWrap:'wrap',gap:8}}>
              {(proj.team||[]).map(m=>(
                <div key={m.id||m.name} style={{background:'white',border:'1px solid #e6e9ef',
                  borderRadius:8,padding:'8px 12px',display:'flex',gap:8,alignItems:'center'}}>
                  <div style={{width:32,height:32,borderRadius:'50%',background:T.primary,color:'white',
                    display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:13}}>
                    {(m.name||'?')[0]}
                  </div>
                  <div>
                    <div style={{fontSize:12.5,fontWeight:700}}>{m.name}</div>
                    <div style={{fontSize:10.5,color:'#676879'}}>{m.role||m.designation||''} · {m.type}</div>
                  </div>
                  <button onClick={async()=>{
                    const upd={...proj,team:(proj.team||[]).filter(t=>t.name!==m.name)};
                    await saveProject(upd);
                  }} style={{background:'none',border:'none',color:T.danger,cursor:'pointer',fontSize:14}}>×</button>
                </div>
              ))}
              {/* Add internal user */}
              <select value="" onChange={async e=>{
                const u=allUsers.find(x=>x.name===e.target.value); if(!u) return;
                if ((proj.team||[]).some(t=>t.name===u.name)) return;
                const upd={...proj,team:[...(proj.team||[]),{...u,type:'Internal QA'}]};
                await saveProject(upd);
              }} style={{padding:'8px 12px',border:'1.5px dashed #e6e9ef',borderRadius:8,
                fontSize:12,color:'#0073ea',background:'white',cursor:'pointer'}}>
                <option value="">+ Add QA Team Member</option>
                {allUsers.filter(u=>!(proj.team||[]).some(t=>t.name===u.name)).map(u=>(
                  <option key={u.id} value={u.name}>{u.name} ({u.role})</option>
                ))}
              </select>
              {/* Add supplier contact */}
              <button onClick={async()=>{
                const name = prompt('Enter supplier contact name:'); if(!name) return;
                const role = prompt('Their role (e.g. Production Manager):');
                const upd={...proj,team:[...(proj.team||[]),{id:uid(),name,role,type:'Supplier Contact'}]};
                await saveProject(upd);
              }} style={{padding:'8px 14px',border:'1.5px dashed #00c875',borderRadius:8,
                fontSize:12,color:'#00c875',background:'white',cursor:'pointer',fontWeight:700}}>
                + Add Supplier Contact
              </button>
            </div>
          </Collapse>
        )}

        {/* Training check */}
        {proj.status!=='Pending Approval' && (proj.team||[]).length>0 && (
          <Collapse title="Training Requirements" defaultOpen>
            <div style={{fontSize:11.5,color:'#676879',marginBottom:10}}>
              Required courses for this project type. Members who already completed a course
              in a previous project are automatically exempted.
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{borderCollapse:'collapse',width:'100%',fontSize:11.5}}>
                <thead><tr style={{background:'#f1f5f9'}}>
                  <th style={{padding:'7px 10px',textAlign:'left',fontWeight:800,borderBottom:'1px solid #e6e9ef'}}>Member</th>
                  <th style={{padding:'7px 10px',fontWeight:800,borderBottom:'1px solid #e6e9ef'}}>Role</th>
                  {(proj.trainingRequired||[]).map(c=>(
                    <th key={c} style={{padding:'7px 10px',fontWeight:700,borderBottom:'1px solid #e6e9ef',
                      fontSize:10.5,textAlign:'center'}}>{c}</th>
                  ))}
                </tr></thead>
                <tbody>{(proj.team||[]).map(m=>(
                  <tr key={m.name} style={{borderBottom:'1px solid #f1f5f9'}}>
                    <td style={{padding:'7px 10px',fontWeight:700}}>{m.name}</td>
                    <td style={{padding:'7px 10px',color:'#676879',fontSize:11}}>{m.type}</td>
                    {(proj.trainingRequired||[]).map(c=>{
                      const status = getTrainingStatus(m, c);
                      return (
                        <td key={c} style={{padding:'7px 10px',textAlign:'center'}}>
                          <span style={{fontSize:10.5,fontWeight:700,
                            color:status.exempt?T.success:T.warning}}>
                            {status.status}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {/* Mark training complete */}
            {isManager(currentUser) && (
              <div style={{marginTop:10,display:'flex',gap:8,flexWrap:'wrap'}}>
                <Btn small outline label="Mark All Training Complete → Move to Active"
                  color={T.success} onClick={async()=>{
                    const upd={...proj,status:'In Progress',trainingDoneAt:new Date().toISOString()};
                    await saveProject(upd); notify('Project moved to active');
                  }}/>
              </div>
            )}
          </Collapse>
        )}

        {/* DMAIC phases */}
        {['Approved','Training','In Progress','Control'].some(s=>proj.status===s) && (
          <div>
            {/* Phase navigator */}
            <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
              {PHASES.map((ph,i)=>(
                <PhasePill key={ph} phase={ph}
                  active={selPhase===ph}
                  done={phaseComplete(ph)&&selPhase!==ph}
                  onClick={()=>setSelPhase(ph)}/>
              ))}
            </div>

            {/* Phase form */}
            <Section title={`${selPhase} Phase`} actions={
              <div style={{display:'flex',gap:8}}>
                {phaseComplete(selPhase) && (
                  <Tag label="✓ Complete" color={PHASE_COLOR[selPhase]}/>
                )}
                {isManager(currentUser) && selPhase==='Control' && phaseComplete('Control') && (
                  <Btn small label="Sign Off → Complete Project" color={T.success}
                    onClick={async()=>{
                      const upd={...proj,status:'Completed',completedAt:new Date().toISOString()};
                      await saveProject(upd); notify('Project marked complete!');
                    }}/>
                )}
              </div>
            }>
              {selPhase==='Define'  && <DefineForm  data={phaseData} onChange={d=>updatePhase('Define',d)}/>}
              {selPhase==='Measure' && <MeasureForm data={phaseData} onChange={d=>updatePhase('Measure',d)}/>}
              {selPhase==='Analyze' && <AnalyzeForm data={phaseData} onChange={d=>updatePhase('Analyze',d)}/>}
              {selPhase==='Improve' && <ImproveForm data={phaseData} onChange={d=>updatePhase('Improve',d)}/>}
              {selPhase==='Control' && <ControlForm data={phaseData} onChange={d=>updatePhase('Control',d)}/>}

              <div style={{marginTop:16,paddingTop:14,borderTop:'1px solid #e6e9ef',
                display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
                {phaseIdx>0 && (
                  <Btn outline small label={`← ${PHASES[phaseIdx-1]}`}
                    color={T.primary} onClick={()=>setSelPhase(PHASES[phaseIdx-1])}/>
                )}
                <div style={{marginLeft:'auto',display:'flex',gap:8}}>
                  {phaseIdx<PHASES.length-1 && (
                    <Btn small label={`${PHASES[phaseIdx+1]} →`}
                      color={PHASE_COLOR[PHASES[phaseIdx+1]]}
                      onClick={async()=>{
                        const nextPhase = PHASES[phaseIdx+1];
                        const upd={...proj,currentPhase:nextPhase,
                          status:nextPhase==='Control'?'Control':'In Progress'};
                        await saveProject(upd);
                        setSelPhase(nextPhase);
                      }}/>
                  )}
                </div>
              </div>
            </Section>
          </div>
        )}

        {/* AI Case Study (Control phase complete) */}
        {proj.status==='Completed' && (
          <Section title="AI Case Study & Success Story" actions={
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              <Btn small label={aiLoading?'Generating…':'🤖 Generate Case Study'}
                color={T.primary} disabled={aiLoading}
                onClick={()=>generateCaseStudy(proj)}/>
              {proj.caseStudy && !stories.some(s=>s.projectId===proj.id) && (
                <Btn small label="🏆 Publish as Success Story" color={T.success}
                  onClick={()=>shareAsStory(proj)}/>
              )}
              {stories.some(s=>s.projectId===proj.id) && (
                <Tag label="✓ Published" color={T.success}/>
              )}
            </div>
          }>
            {proj.caseStudy ? (
              <div style={{background:'#fafbfc',border:'1px solid #e6e9ef',borderRadius:8,padding:16}}>
                <pre style={{whiteSpace:'pre-wrap',fontFamily:'inherit',fontSize:12.5,
                  lineHeight:1.7,color:'#1f1f1f',margin:0}}>
                  {proj.caseStudy}
                </pre>
              </div>
            ) : (
              <div style={{textAlign:'center',padding:30,color:'#676879',fontSize:12}}>
                Click "Generate Case Study" to have AI create a professional case study
                and cost-benefit analysis from your DMAIC data.
              </div>
            )}
          </Section>
        )}
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // SUCCESS STORIES
  // ════════════════════════════════════════════════════════════════
  const SuccessStories = () => (
    <div>
      {stories.length===0 ? (
        <div style={{textAlign:'center',padding:60,color:'#676879'}}>
          <div style={{fontSize:48,marginBottom:12}}>🏆</div>
          <div style={{fontSize:14,fontWeight:700}}>No success stories yet</div>
          <div style={{fontSize:12,marginTop:6}}>
            Complete a project and publish it as a success story.
          </div>
        </div>
      ) : stories.map(s=>(
        <div key={s.id} style={{background:'white',border:'1px solid #e6e9ef',
          borderRadius:12,overflow:'hidden',marginBottom:14,
          boxShadow:'0 1px 4px rgba(0,0,0,.05)'}}>
          <div style={{background:'linear-gradient(135deg,#12161c,#0073ea)',
            padding:'14px 18px',color:'white'}}>
            <div style={{fontWeight:900,fontSize:15}}>🏆 {s.title}</div>
            <div style={{fontSize:11,opacity:0.8,marginTop:2}}>
              {(s.suppliers||[]).join(', ')} · {s.category} · Shared by {s.sharedBy}
              · {s.sharedAt?.split('T')[0]}
            </div>
          </div>
          <div style={{padding:'12px 16px'}}>
            <div style={{display:'flex',gap:16,flexWrap:'wrap',marginBottom:12}}>
              {[['Result',s.result||'—'],['Improvement',s.improvement||'—'],
                ['Savings',s.savings||'—']].map(([l,v])=>(
                <div key={l} style={{background:'#f0fdf4',borderRadius:8,padding:'8px 14px',minWidth:120}}>
                  <div style={{fontSize:9.5,color:'#676879',textTransform:'uppercase',letterSpacing:'0.05em'}}>{l}</div>
                  <div style={{fontSize:16,fontWeight:900,color:T.success,marginTop:2}}>{v}</div>
                </div>
              ))}
            </div>
            {s.caseStudy && (
              <details>
                <summary style={{cursor:'pointer',fontSize:12,fontWeight:700,color:T.primary,
                  padding:'6px 0',listStyle:'none',display:'flex',alignItems:'center',gap:4}}>
                  📖 Read Full Case Study
                </summary>
                <div style={{marginTop:10,background:'#fafbfc',border:'1px solid #e6e9ef',
                  borderRadius:8,padding:'14px 16px'}}>
                  <pre style={{whiteSpace:'pre-wrap',fontFamily:'inherit',fontSize:12,
                    lineHeight:1.7,color:'#1f1f1f',margin:0}}>
                    {s.caseStudy}
                  </pre>
                </div>
              </details>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // ════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════════════════════════
  if (view==='request') return (
    <div style={{padding:'20px 24px 60px'}}>
      <RequestForm/>
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );

  if (view==='detail' && selProj) return (
    <div style={{padding:'20px 24px 60px'}}>
      <ProjectDetail proj={projects.find(p=>p.id===selProj.id)||selProj}/>
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );

  // List view
  const TABS = ['ACTIVE','PENDING APPROVAL','COMPLETED','SUCCESS STORIES'];
  const listMap = {
    'ACTIVE': active, 'PENDING APPROVAL': pending,
    'COMPLETED': completed, 'SUCCESS STORIES': [],
  };
  const listRows = listMap[tab]||[];

  return (
    <div style={{padding:'20px 24px 60px'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
        flexWrap:'wrap',gap:12,marginBottom:20,paddingBottom:16,borderBottom:'2px solid #e6e9ef'}}>
        <div>
          <div style={{fontSize:20,fontWeight:900,color:'#1b1f26',
            display:'flex',alignItems:'center',gap:10}}>
            <span style={{width:4,height:22,background:'linear-gradient(180deg,#0073ea,#00c875)',
              borderRadius:2,display:'inline-block'}}/>
            QUALITY IMPROVEMENT PROJECTS
          </div>
          <div style={{fontSize:12.5,color:'#6b7280',marginTop:3}}>
            DMAIC methodology · Structured problem solving · Success stories
          </div>
        </div>
        <Btn label="+ New Project Request" color={T.primary} onClick={()=>setView('request')}/>
      </div>

      {/* KPIs */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',
        gap:12,marginBottom:20}}>
        <StatCard label="Active Projects" value={active.length} color={T.primary}/>
        <StatCard label="Pending Approval" value={pending.length}
          color={pending.length>0?T.warning:T.success}/>
        <StatCard label="Completed" value={completed.length} color={T.success}/>
        <StatCard label="Success Stories" value={stories.length} color='#fdab3d'/>
        <StatCard label="Suppliers Involved"
          value={[...new Set(projects.flatMap(p=>p.suppliers||[]))].length} color={T.teal}/>
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab}/>

      {tab==='SUCCESS STORIES' ? <SuccessStories/> : (
        listRows.length===0 ? (
          <div style={{textAlign:'center',padding:60,color:'#676879'}}>
            <div style={{fontSize:40,marginBottom:12}}>
              {tab==='ACTIVE'?'📋':tab==='PENDING APPROVAL'?'⏳':'✅'}
            </div>
            <div style={{fontSize:14,fontWeight:700}}>No {tab.toLowerCase()} projects</div>
            {tab==='ACTIVE' && (
              <div style={{fontSize:12,marginTop:8}}>
                <button onClick={()=>setView('request')}
                  style={{color:T.primary,background:'none',border:'none',
                    cursor:'pointer',fontSize:12,fontWeight:700,textDecoration:'underline'}}>
                  Start a new project request →
                </button>
              </div>
            )}
          </div>
        ) : (
          <div>
            {listRows.map(proj=>{
              const phaseDone = PHASES.filter(ph=>{
                const d=proj.phases?.[ph]||{};
                if(ph==='Define')  return !!(d.problemStatement&&d.goalStatement);
                if(ph==='Measure') return !!(d.baseline);
                if(ph==='Analyze') return !!(d.rootCause);
                if(ph==='Improve') return !!(d.resultAfter);
                if(ph==='Control') return !!(d.finalResult);
                return false;
              });
              return (
                <div key={proj.id} onClick={()=>{setSelProj(proj);setView('detail');
                  setSelPhase(proj.currentPhase||'Define');}}
                  style={{background:'white',border:'1px solid #e6e9ef',borderRadius:12,
                    padding:'14px 16px',marginBottom:12,cursor:'pointer',
                    boxShadow:'0 1px 4px rgba(0,0,0,.04)',
                    transition:'box-shadow .15s,transform .15s'}}
                  onMouseEnter={e=>{e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,.1)';
                    e.currentTarget.style.transform='translateY(-1px)';}}
                  onMouseLeave={e=>{e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,.04)';
                    e.currentTarget.style.transform='none';}}>
                  <div style={{display:'flex',justifyContent:'space-between',
                    flexWrap:'wrap',gap:8,marginBottom:10}}>
                    <div>
                      <div style={{fontWeight:900,fontSize:14,color:'#1b1f26'}}>{proj.title}</div>
                      <div style={{fontSize:11.5,color:'#676879',marginTop:2}}>
                        {(proj.suppliers||[]).join(', ')} · {proj.category}
                        · Requested by {proj.requestedBy} · {proj.requestedAt?.split('T')[0]}
                      </div>
                    </div>
                    <span style={{padding:'4px 12px',borderRadius:10,fontSize:11,fontWeight:800,
                      background:`${STATUS_COLOR[proj.status]||'#8a94a2'}18`,
                      color:STATUS_COLOR[proj.status]||'#8a94a2',whiteSpace:'nowrap'}}>
                      {proj.status}
                    </span>
                  </div>
                  {/* DMAIC progress pills */}
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {PHASES.map(ph=>(
                      <span key={ph} style={{padding:'3px 10px',borderRadius:10,fontSize:10.5,
                        fontWeight:700,
                        background:phaseDone.includes(ph)?PHASE_COLOR[ph]:
                          proj.currentPhase===ph?`${PHASE_COLOR[ph]}22`:'#f1f5f9',
                        color:phaseDone.includes(ph)?'white':
                          proj.currentPhase===ph?PHASE_COLOR[ph]:'#8a94a2'}}>
                        {phaseDone.includes(ph)?'✓ ':proj.currentPhase===ph?'● ':''}{ph}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );
}
